import React, {useState, useEffect} from "react"
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconCheck,
  IconX,
  IconShield,
} from "@tabler/icons-react"
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tooltip,
  useDisclosure,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Chip,
  Divider,
  Select,
  SelectItem,
  Switch,
} from "@heroui/react"
import {inviteUser} from "@/utils/api/functions/invite-user"
import {removeUser} from "@/utils/api/functions/remove-user"
import {useUser} from "@/contexts/UserContextProvider"
import {useOrgUsers, useCurrentUserData} from "@/utils/hooks/useOrgUsers"
import {EditUserModal} from "./EditUserModal"

export function UserManagementTab() {
  const {user, session, orgId} = useUser()
  const [dataLoader, setDataLoader] = useState(false)
  const [inviteSubmitting, setInviteSubmitting] = useState(false)
  const [inviteError, setInviteError] = useState<string | undefined>()

  const {users: orgUsers, loading, error, refetch} = useOrgUsers()
  const {isAdmin: isCurrentUserAdmin} = useCurrentUserData()

  useEffect(() => {
    console.log("DATA CHANGED?", orgUsers)
  }, [orgUsers])

  const {isOpen, onOpen, onOpenChange} = useDisclosure()

  const [userName, setUserName] = useState("")
  const [userEmail, setUserEmail] = useState("")

  // Invite settings state
  const [inviteRole, setInviteRole] = useState("MEMBER")
  // Does the admin have access to sensitive communications? Defaults to true (level 5).
  const [inviteHasSensitiveAccess, setInviteHasSensitiveAccess] = useState(true)
  const [inviteRequiresIntegrations, setInviteRequiresIntegrations] =
    useState(true)

  // Edit modal state
  const [editUser, setEditUser] = useState<any>(null)
  const [isEditOpen, setIsEditOpen] = useState(false)

  // Delete user modal state
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onOpenChange: onDeleteOpenChange,
  } = useDisclosure()
  const [deleteUserTarget, setDeleteUserTarget] = useState<any>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState<string | undefined>()

  // Table columns definition
  const columns = [
    {key: "name", label: "Name"},
    {key: "email", label: "Email"},
    {key: "sensitivity", label: "Sensitivity"},
    {key: "role", label: "Role"},
    {key: "integrations", label: "Integrations"},
    {key: "status", label: "Status"},
    {key: "actions", label: "Actions"},
  ]

  // Helper for table rendering
  function getSensitivityLabel(level: number) {
    if (level === 0) return "No Access"
    if (level === 1) return "Level 1"
    if (level === 2) return "Level 2"
    if (level === 3) return "Level 3"
    if (level === 4) return "Level 4"
    if (level === 5) return "Level 5 (Full)"
    return `Level ${level}`
  }

  function getSensitivityChip(level: number) {
    const label = getSensitivityLabel(level)
    let color: "default" | "warning" | "primary" | "danger" = "default"
    if (level === 0) color = "default"
    else if (level <= 2) color = "warning"
    else if (level <= 4) color = "primary"
    else color = "danger"

    return (
      <Chip
        variant="flat"
        color={color}
        size="sm"
        startContent={<IconShield size={14} />}
      >
        {label}
      </Chip>
    )
  }

  function getChipColor(status: string) {
    if (
      status === "Connected" ||
      status === "Accepted" ||
      status === "Complete"
    )
      return "success"
    if (status === "Missing" || status === "Incomplete" || status === "Pending")
      return "danger"
    return "default"
  }

  // Prepare table items
  const items = (orgUsers || []).map((user) => {
    // Integrations
    const integrationRequirements =
      (user.integration_requirements as any[]) || []
    const integrations = (user.integrations as Record<string, any>) || {}
    // Integration chips
    const integrationChips = integrationRequirements.map((req) => {
      const label = req.name || `${req.provider}/${req.channel}`
      const status = req.is_fulfilled ? "Connected" : "Missing"
      return (
        <Chip
          key={req.requirement_id}
          color={getChipColor(status)}
          variant="flat"
          size="sm"
          className="mb-1 mr-1"
          startContent={
            req.is_fulfilled ? <IconCheck size={14} /> : <IconX size={14} />
          }
        >
          {label}: {status}
        </Chip>
      )
    })
    // Status chips
    const statusChips = []
    if (user.invite_accepted !== undefined)
      statusChips.push(
        <Chip
          key="invite"
          color={getChipColor(user.invite_accepted ? "Accepted" : "Pending")}
          variant="flat"
          size="sm"
          className="mb-1 mr-1"
          startContent={
            user.invite_accepted ? <IconCheck size={14} /> : <IconX size={14} />
          }
        >
          Invite: {user.invite_accepted ? "Accepted" : "Pending"}
        </Chip>
      )
    // Determine WORM reconciliation status across all integrations that report it
    const reconciliationValues = Object.values(integrations)
      .filter(
        (i: any) =>
          i &&
          i.reconciliation_completed !== null &&
          i.reconciliation_completed !== undefined
      )
      .map((i: any) => i.reconciliation_completed as boolean)

    // if (reconciliationValues.length > 0) {
    //   const allComplete = reconciliationValues.every((val) => val)
    //   statusChips.push(
    //     <Chip
    //       key="reconciliation"
    //       color={getChipColor(allComplete ? "Complete" : "Incomplete")}
    //       variant="flat"
    //       size="sm"
    //       className="mb-1 mr-1"
    //       startContent={
    //         allComplete ? <IconCheck size={14} /> : <IconX size={14} />
    //       }
    //     >
    //       WORM Reconciliation: {allComplete ? "Complete" : "Incomplete"}
    //     </Chip>
    //   )
    // }
    return {
      key: user.auth_user_id,
      name: user.full_name,
      email: user.email,
      sensitivity: getSensitivityChip(user.sensitivity_read_level),
      role: user.role
        ? user.role.charAt(0).toUpperCase() + user.role.slice(1).toLowerCase()
        : "Member",
      integrations:
        integrationChips.length > 0 ? (
          integrationChips
        ) : (
          <span className="text-gray-400">None</span>
        ),
      status:
        statusChips.length > 0 ? (
          statusChips
        ) : (
          <span className="text-gray-400">None</span>
        ),
      actions: (
        <div className="flex min-h-[32px] items-center justify-end gap-2">
          {isCurrentUserAdmin ? (
            <>
              <Tooltip content="Edit Settings" placement="bottom-end">
                <Button
                  variant="flat"
                  color="primary"
                  isIconOnly
                  radius="full"
                  size="sm"
                  startContent={<IconEdit size={14} />}
                  className="max-h-fit w-fit min-w-fit max-w-fit p-2"
                  onClick={() => {
                    setEditUser(user)
                    setIsEditOpen(true)
                  }}
                />
              </Tooltip>
              <Tooltip content="Remove User" placement="bottom-end">
                <Button
                  variant="flat"
                  color="danger"
                  isIconOnly
                  radius="full"
                  size="sm"
                  startContent={<IconTrash size={14} />}
                  className="max-h-fit w-fit min-w-fit max-w-fit p-2"
                  onClick={() => {
                    setDeleteUserTarget(user)
                    setDeleteError(undefined)
                    onDeleteOpen()
                  }}
                />
              </Tooltip>
            </>
          ) : null}
        </div>
      ),
    }
  })

  const handleSubmit = async (userName: string, userEmail: string) => {
    setInviteSubmitting(true)
    setInviteError(undefined)

    try {
      await inviteUser({
        userName,
        userEmail,
        session,
        role: inviteRole,
        sensitivityReadLevel:
          inviteRole === "ADMIN" && inviteHasSensitiveAccess ? 5 : 0,
        isWhitelisted:
          inviteRole === "ADMIN" ? inviteRequiresIntegrations : true, // If not admin, default to true
      })

      // Reset form state on success
      setUserName("")
      setUserEmail("")
      setInviteRole("MEMBER")
      setInviteHasSensitiveAccess(true)
      setInviteRequiresIntegrations(true)

      setDataLoader((prev) => !prev)
      await refetch()

      return true
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to invite user"
      setInviteError(message)
      return false
    } finally {
      setInviteSubmitting(false)
    }
  }

  // Delete handler
  const handleDelete = async () => {
    if (!deleteUserTarget) return
    setDeleteLoading(true)
    setDeleteError(undefined)

    try {
      if (!orgId) {
        throw new Error("Organization ID not found")
      }

      await removeUser({
        authUserToRemove: deleteUserTarget.auth_user_id,
        organizationId: orgId,
        session,
      })

      setDeleteUserTarget(null)
      setDataLoader((prev) => !prev)
      await refetch()
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to remove user"
      setDeleteError(message)
    } finally {
      setDeleteLoading(false)
    }
  }

  return (
    <div className="relative flex w-full flex-col rounded-lg border border-black/5 bg-white p-6 shadow-sm">
      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        backdrop="transparent"
        size="sm"
        classNames={{
          base: "absolute right-0 top-6",
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <p className="text-lg font-medium">Invite New User</p>
                {/* <p className="text-sm font-normal">
                  Add a new user to your organization by entering their name and
                  email address.
                </p> */}
              </ModalHeader>
              <ModalBody className="gap-4">
                {inviteError && (
                  <p className="text-sm text-red-600">{inviteError}</p>
                )}
                <Input
                  label="Full Name"
                  value={userName}
                  isRequired
                  onChange={(e) => setUserName(e.target.value)}
                />
                <Input
                  label="Email"
                  value={userEmail}
                  type="email"
                  isRequired
                  isInvalid={!userEmail.includes("@") && userEmail !== ""}
                  onChange={(e) => setUserEmail(e.target.value)}
                />

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium">Role</label>
                  <Select
                    label="Select Role"
                    selectedKeys={[inviteRole]}
                    onSelectionChange={(keys) => {
                      const key = Array.from(keys)[0] as string
                      setInviteRole(key)
                    }}
                  >
                    <SelectItem key="MEMBER">Member</SelectItem>
                    <SelectItem key="ADMIN">Admin</SelectItem>
                  </Select>
                </div>

                {inviteRole === "ADMIN" && (
                  <>
                    {/* Sensitive Access Toggle */}
                    <div className="flex w-full flex-col gap-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">Sensitive Read Access</p>
                        <Switch
                          isSelected={inviteHasSensitiveAccess}
                          onValueChange={setInviteHasSensitiveAccess}
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        Should this admin have access to communications marked
                        as sensitive?
                      </p>
                    </div>

                    {/* Communications Archiving Toggle */}
                    <div className="flex w-full flex-col gap-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">
                          Communications archiving required
                        </p>
                        <Switch
                          isSelected={inviteRequiresIntegrations}
                          onValueChange={setInviteRequiresIntegrations}
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        Does this admin also need their communications
                        monitored?
                      </p>
                    </div>
                  </>
                )}
              </ModalBody>
              <ModalFooter>
                <Button
                  color="primary"
                  size="sm"
                  isDisabled={!userName || !userEmail}
                  onPress={async () => {
                    const success = await handleSubmit(userName, userEmail)
                    if (success) onClose()
                  }}
                  isLoading={inviteSubmitting}
                >
                  Add User
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <div className="mb-4 flex flex-row items-center justify-between">
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold">User Management</h2>
          <span className="text-xs text-gray-600">
            {orgUsers?.length || 0} Whitelisted Users
          </span>
        </div>
        <Tooltip content="Add User" placement="bottom-end">
          <Button
            variant="flat"
            color="primary"
            isIconOnly
            radius="full"
            startContent={<IconPlus size={16} />}
            className="max-h-fit w-fit min-w-fit max-w-fit p-2"
            onClick={onOpen}
          ></Button>
        </Tooltip>
      </div>

      <div className="overflow-x-auto">
        <Table
          aria-label="User Management Table"
          removeWrapper
          className="min-w-full text-sm"
          isStriped
        >
          <TableHeader>
            <TableColumn key="name">Name</TableColumn>
            <TableColumn key="email">Email</TableColumn>
            <TableColumn key="sensitivity">Sensitivity</TableColumn>
            <TableColumn key="role">Role</TableColumn>
            <TableColumn key="integrations">Integrations</TableColumn>
            <TableColumn key="status">Status</TableColumn>
            <TableColumn key="actions" align="end">
              Actions
            </TableColumn>
          </TableHeader>
          <TableBody items={items}>
            {(row) => (
              <TableRow key={row.key} className="even:bg-gray-50">
                <TableCell className="py-1.5 align-middle">
                  {row.name}
                </TableCell>
                <TableCell className="py-1.5 align-middle">
                  {row.email}
                </TableCell>
                <TableCell className="py-1.5 align-middle">
                  {row.sensitivity}
                </TableCell>
                <TableCell className="py-1.5 align-middle">
                  {row.role}
                </TableCell>
                <TableCell className="max-w-xs py-1.5 align-middle">
                  <div className="flex flex-wrap gap-x-2 gap-y-1 truncate">
                    {row.integrations}
                  </div>
                </TableCell>
                <TableCell className="max-w-xs py-1.5 align-middle">
                  <div className="flex flex-wrap gap-x-2 gap-y-1 truncate">
                    {row.status}
                  </div>
                </TableCell>
                <TableCell className="py-1.5 align-middle">
                  <div className="flex items-center justify-end gap-2">
                    {row.actions}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <EditUserModal
        isOpen={isEditOpen}
        onOpenChange={(open) => {
          setIsEditOpen(open)
          if (!open) setEditUser(null)
        }}
        user={editUser}
        initialSensitivity={editUser?.sensitivity_read_level || 0}
        initialRole={editUser?.role || "MEMBER"}
        initialWhitelisted={editUser?.is_whitelisted ?? true}
        onUpdated={() => refetch()}
      />

      {/* Delete User Modal */}
      <Modal
        isOpen={isDeleteOpen}
        onOpenChange={onDeleteOpenChange}
        backdrop="transparent"
        size="sm"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <p className="text-lg font-medium">Delete User</p>
              </ModalHeader>
              <ModalBody className="gap-4">
                {deleteError && (
                  <p className="text-sm text-red-600">{deleteError}</p>
                )}
                <p className="text-sm font-normal">
                  Are you sure you want to delete{" "}
                  <strong>{deleteUserTarget?.full_name}</strong>? This action
                  cannot be undone.
                </p>
              </ModalBody>
              <ModalFooter>
                <Button
                  variant="light"
                  onPress={onClose}
                  isDisabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  color="danger"
                  isLoading={deleteLoading}
                  onPress={async () => {
                    await handleDelete()
                    onClose()
                  }}
                >
                  Delete User
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  )
}
